import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Chess} from 'chess.js';
import StockfishEngine from '@/Components/StockfishEngine';
import type {CevalPvLine} from '@/Components/CevalPanel';
import type {AutoShape} from '@/Components/ChessboardWithArrow/EngineShapesOverlay';
import {getLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';
import {sanitizeFen, winningChances} from '../utils/studyHelpers';

// Local ceval search budget. The engine layer additionally caps each search
// with a 10s movetime watchdog, so this is the "Depth n/24" ceiling the
// panel reports, not a promise a slow device must reach.
export const CEVAL_MAX_DEPTH = 24;

export type StockfishDisplayResult = {
  score: string;
  isMate: boolean;
  baseTurn: 'w' | 'b';
  rawScore: string;
  // White-POV value normalized once at ingestion (see handleCevalInfo):
  // centipawns when !isMate, signed mate-in-N when isMate (positive = White
  // mates). The bar and the score text are both derived from this single
  // value so they can never disagree - see EvalBar.
  whitePovValue: number;
};

type UseEngineAnalysisArgs = {
  analysisFen: string;
  // Resets threat mode and PV state when the chapter switches.
  chapterId: any;
  // Locked lesson/practice chapters must not leak best-move/threat arrows
  // to the student even while the engine runs for the practice opponent.
  isChapterLocked: boolean;
  // Practice mode always runs a fixed single-line budget, matching
  // Lichess's studyPracticeCtrl (multiPv: 1) - it must not follow the
  // student's own Analysis-tab MultiPV preference.
  practiceMode: boolean;
  // True while a practice game is running and the viewer hasn't opted back
  // into engine help. Suppresses the board arrows only - the engine keeps
  // searching, so the opponent still moves and move verdicts still land.
  assistSuppressed: boolean;
  // Strength cap for the practice OPPONENT only (null/undefined = full
  // strength). The analysis engine above always searches at full strength -
  // the eval bar and the move verdicts would be worthless otherwise - so the
  // opponent is a second, separately-configured engine instance rather than a
  // weakened version of the analysis one.
  practiceElo?: number | null;
  practiceDepth?: number;
  practiceMovetime?: number;
  // Which colour the human is playing. Used to run the opponent engine only
  // on the opponent's own turn — see opponentEngineElement.
  practiceStudentColor?: 'w' | 'b';
  // Top-line result callback, fired once per depth on the primary PV -
  // the screen uses it for the eval bar and practice-verdict
  // classification. `null` means the engine was turned off. `searchedFen` is
  // the position the line was actually computed for; consumers that ACT on a
  // result must key off it rather than their own current selection.
  onResult: (
    result: StockfishDisplayResult | null,
    searchedFen?: string,
  ) => void;
};

// Lichess-style local eval (ceval): the main engine always searches the
// real position; threat mode ("x") runs a second, independent search with
// the move passed to the opponent, so the best-move arrow and the threat
// arrow can be shown at the same time. The engines run headlessly at the
// screen level (see cevalEngineElement/threatEngineElement) so practice
// mode keeps its opponent even when the Analysis tab isn't mounted;
// CevalPanel is purely presentational.
export function useEngineAnalysis({
  analysisFen,
  chapterId,
  isChapterLocked,
  practiceMode,
  assistSuppressed,
  practiceElo,
  practiceDepth,
  practiceMovetime,
  practiceStudentColor,
  onResult,
}: UseEngineAnalysisArgs) {
  const [enableStockfish, setEnableStockfish] = useState(false);
  // The practice opponent's own top line, kept apart from cevalPvs so a
  // deliberately-weak opponent search can never contaminate the analysis
  // eval, the eval bar, or the best-move arrows.
  const [opponentPvUci, setOpponentPvUci] = useState<string | null>(null);
  const [cevalPvs, setCevalPvs] = useState<CevalPvLine[]>([]);
  const [cevalDepth, setCevalDepth] = useState(0);
  // Threat mode's own independent PV array - its lines never overwrite the
  // real position's cevalPvs above; that overwrite was why the own-best-
  // move arrow used to disappear whenever the threat arrow was turned on.
  const [threatPvs, setThreatPvs] = useState<CevalPvLine[]>([]);
  // "Multiple lines" (MultiPV) + "Best move arrow" — the two engine
  // preferences lichess exposes; persisted like the explorer config.
  const [cevalMultiPv, setCevalMultiPv] = useState(1);
  const [showBestMoveArrow, setShowBestMoveArrow] = useState(true);
  const [threatMode, setThreatMode] = useState(false);
  // First move of a PV row the user is hovering — previewed as an arrow.
  const [hoveredPvUci, setHoveredPvUci] = useState<string | null>(null);

  // Threat mode analyzes the current position with the turn handed to the
  // opponent (en passant cleared — it's no longer meaningful after a null
  // move). Unavailable while in check or at game over, where a null move
  // makes no sense; lichess hides threat the same way.
  const threatFen = useMemo(() => {
    const realFen = sanitizeFen(analysisFen);
    try {
      const chess = new Chess(realFen);
      if (chess.isCheck() || chess.isGameOver()) {
        return null;
      }
    } catch (e) {
      return null;
    }
    const parts = realFen.split(' ');
    if (parts.length < 2) {
      return null;
    }
    parts[1] = parts[1] === 'w' ? 'b' : 'w';
    if (parts.length > 3) {
      parts[3] = '-';
    }
    return parts.join(' ');
  }, [analysisFen]);

  const effectiveThreatMode = threatMode && !!threatFen && enableStockfish;
  const cevalFen = sanitizeFen(analysisFen);

  const cevalGameOver = useMemo(() => {
    try {
      return new Chess(sanitizeFen(analysisFen)).isGameOver();
    } catch (e) {
      return false;
    }
  }, [analysisFen]);

  // Deliberately does NOT clear cevalPvs when cevalFen/threat/multiPv change:
  // the engine's own search-token filtering (engine.ts) already guarantees
  // every line we receive belongs to the CURRENT search, so a stale line
  // from the previous position can never leak in. Blanking the array here
  // instead just makes the board arrow (and eval) flash to nothing for the
  // ~100ms-plus it takes the fresh search to report its first line -
  // most noticeably when toggling threat mode, where it looked like the
  // arrow "didn't show" at all. Leaving the last-known line in place until
  // it's naturally overwritten is what lichess's own ceval display does too.
  useEffect(() => {
    if (!enableStockfish) {
      setCevalPvs([]);
      setCevalDepth(0);
      setThreatPvs([]);
      setHoveredPvUci(null);
    }
  }, [enableStockfish]);

  useEffect(() => {
    setThreatMode(false);
    setCevalPvs([]);
    setCevalDepth(0);
    setThreatPvs([]);
  }, [chapterId]);

  // Restore the user's engine preferences once, then persist changes.
  const cevalConfigLoadedRef = useRef(false);
  useEffect(() => {
    (async () => {
      const stored = (await getLocalStorage('study:ceval-config')) as {
        multiPv?: number;
        showBestMoveArrow?: boolean;
      } | null;
      if (stored) {
        if (
          typeof stored.multiPv === 'number' &&
          stored.multiPv >= 1 &&
          stored.multiPv <= 5
        ) {
          setCevalMultiPv(stored.multiPv);
        }
        if (typeof stored.showBestMoveArrow === 'boolean') {
          setShowBestMoveArrow(stored.showBestMoveArrow);
        }
      }
      cevalConfigLoadedRef.current = true;
    })();
  }, []);
  useEffect(() => {
    if (cevalConfigLoadedRef.current) {
      setLocalStorage('study:ceval-config', {
        multiPv: cevalMultiPv,
        showBestMoveArrow,
      });
    }
  }, [cevalMultiPv, showBestMoveArrow]);

  // One `info` line from the engine -> one PV row. StockfishEngine keeps its
  // onResult ref fresh every render, so this closure always sees current
  // state without a useCallback dependency dance.
  const handleCevalInfo = (
    variationNumber: number,
    movesSAN: string[],
    baseTurn: 'w' | 'b',
    score: string,
    isMate: boolean,
    raw: string,
    searchedFen?: string,
  ) => {
    const depthMatch = raw.match(/\bdepth (\d+)/);
    const lineDepth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
    const pvIdx = raw.indexOf(' pv ');
    const uciMoves =
      pvIdx === -1
        ? []
        : raw
            .slice(pvIdx + 4)
            .trim()
            .split(/\s+/);
    if (!movesSAN.length || !uciMoves.length) {
      return;
    }
    // Standard UCI scores are side-to-move POV; normalize to white POV here,
    // once, and reuse the same value for the PV line, the display string,
    // and (via whitePovValue) the eval bar - a second sign flip anywhere
    // downstream would double-apply and break black-to-move display.
    const sign = baseTurn === 'b' ? -1 : 1;
    const numeric = Number(score) || 0;
    const whitePovValue = sign * numeric;
    const line: CevalPvLine = {
      sanMoves: movesSAN,
      uciMoves,
      depth: lineDepth,
      ...(isMate ? {mate: whitePovValue} : {cp: whitePovValue}),
    };
    setCevalPvs(prev => {
      const next = prev.slice(0, cevalMultiPv);
      next[variationNumber - 1] = line;
      return next;
    });
    if (variationNumber !== 1) {
      return;
    }
    setCevalDepth(lineDepth);
    let displayScore: string;
    if (isMate) {
      displayScore = whitePovValue === 0 ? '#' : `M${Math.abs(whitePovValue)}#`;
    } else {
      displayScore = (whitePovValue / 100).toFixed(2);
    }
    // whitePovValue (master's eval-sign-black fix) and searchedFen (this
    // branch's stale-position guard) are independent additions to the same
    // call - both are required by consumers now, so both travel together.
    onResult(
      {
        score: displayScore,
        isMate,
        baseTurn,
        rawScore: score,
        whitePovValue,
      },
      searchedFen,
    );
  };

  // Threat mode's own `info` line handler — a completely independent PV
  // array from handleCevalInfo/cevalPvs above, so the threat search (run by
  // threatEngineElement against threatFen) never overwrites the real
  // position's best-move line.
  const handleThreatCevalInfo = (
    variationNumber: number,
    movesSAN: string[],
    baseTurn: 'w' | 'b',
    score: string,
    isMate: boolean,
    raw: string,
  ) => {
    const depthMatch = raw.match(/\bdepth (\d+)/);
    const lineDepth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
    const pvIdx = raw.indexOf(' pv ');
    const uciMoves =
      pvIdx === -1
        ? []
        : raw
            .slice(pvIdx + 4)
            .trim()
            .split(/\s+/);
    if (!movesSAN.length || !uciMoves.length) {
      return;
    }
    const sign = baseTurn === 'b' ? -1 : 1;
    const numeric = Number(score) || 0;
    const line: CevalPvLine = {
      sanMoves: movesSAN,
      uciMoves,
      depth: lineDepth,
      ...(isMate ? {mate: sign * numeric} : {cp: sign * numeric}),
    };
    setThreatPvs(prev => {
      const next = prev.slice(0, 1);
      next[variationNumber - 1] = line;
      return next;
    });
  };

  // Best move of the real position's top line — the "Best move!" verdict and
  // the board arrows hang off this. NOT what the practice opponent plays; see
  // opponentBestMoveUci.
  const nextBestMoveUci = useMemo(
    () => cevalPvs[0]?.uciMoves?.[0] || null,
    [cevalPvs],
  );

  // The practice opponent's reply for the current position, from its own
  // strength-limited search. Cleared whenever the position changes so the
  // Study screen can never replay the previous position's move.
  //
  // Read from `bestmove`, NOT from the top `info ... pv` line. Stockfish
  // applies the UCI_Elo handicap when it picks bestmove (Skill::pick_best
  // swaps a deliberately sub-optimal root move in at the end of the search)
  // while the info PVs keep reporting the objectively best line it found.
  // Taking the PV's first move - which is what this used to do - meant the
  // opponent played the engine's top move at every level, so the difficulty
  // setting changed only how long it thought, never what it played.
  const handleOpponentBestMove = (uci: string, searchedFen: string) => {
    if (searchedFen && sanitizeFen(searchedFen) !== cevalFen) {
      return;
    }
    if (uci && uci.length >= 4) {
      setOpponentPvUci(uci);
    }
  };

  // The opponent engine still reports info lines; they drive nothing (its
  // eval is deliberately weakened, so it must never feed the eval bar or the
  // move verdicts - that's the analysis engine's job). Kept as an explicit
  // no-op because `onResult` is a required prop.
  const handleOpponentInfo = () => {};

  useEffect(() => {
    setOpponentPvUci(null);
  }, [cevalFen, practiceMode]);

  const toggleThreatMode = useCallback(() => {
    if (!enableStockfish) {
      return;
    }
    setThreatMode(prev => {
      const next = !prev;
      if (!next) {
        setThreatPvs([]);
      }
      return next;
    });
  }, [enableStockfish]);

  const handleCevalToggle = (value: boolean) => {
    // Switching the engine off during a practice game used to stop the
    // opponent dead - `engineOn` for all three instances hangs off this flag,
    // and the screen only force-enables it on entering a practice chapter, so
    // nothing ever turned it back on. The game silently hung, and because
    // `isEngineThinking` is also gated on it the UI didn't even say "thinking".
    // Hiding the engine's OUTPUT mid-game is what the practice panel's
    // "Engine assistance" switch is for; this one has to stay on.
    if (practiceMode && !value) {
      return;
    }
    setEnableStockfish(value);
    if (!value) {
      onResult(null);
      setThreatMode(false);
      setThreatPvs([]);
      setHoveredPvUci(null);
    }
  };

  // Engine arrows, lichess-style. The normal best-move arrow (blue, from
  // cevalPvs — always the real position) and the threat arrow (red, from
  // threatPvs — the independent threat-mode search) are drawn from two
  // separate PV arrays, so turning threat mode on adds the red arrow
  // alongside the blue one instead of replacing it. A hovered PV row
  // previews at full strength on top of both.
  const engineAutoShapes = useMemo((): AutoShape[] => {
    // Practice mode force-enables enableStockfish internally so the computer
    // opponent can move, but the resulting best-move/threat arrows would then
    // sit on the board pointing at the move you're being asked to find.
    //
    // `isChapterLocked` alone didn't cover this: it's false for a Sandbox on
    // an ordinary analysis chapter, false for the owner testing their own
    // exercise, and false again once a chapter has been completed and the
    // student hits "Play again" - so the arrows came back in exactly the
    // situations where someone is actually playing.
    //
    // assistSuppressed is the caller's "a game is running and the viewer
    // hasn't asked for help" flag, so turning assistance back on restores the
    // arrows mid-game without touching the viewer's own arrow preference.
    if (!enableStockfish || isChapterLocked || assistSuppressed) {
      return [];
    }
    const shapes: AutoShape[] = [];
    const seen = new Set<string>();
    const topUci = cevalPvs[0]?.uciMoves?.[0];

    if (showBestMoveArrow && topUci && topUci.length >= 4) {
      const key = topUci.slice(0, 4);
      seen.add(key);
      shapes.push({
        orig: topUci.slice(0, 2),
        dest: topUci.slice(2, 4),
        brush: 'paleBlue',
      });

      const bestWc = winningChances(cevalPvs[0]);
      cevalPvs.slice(1).forEach(pv => {
        const uci = pv?.uciMoves?.[0];
        if (!uci || uci.length < 4) {
          return;
        }
        const altKey = uci.slice(0, 4);
        if (seen.has(altKey)) {
          return;
        }
        seen.add(altKey);
        const gap = Math.abs(bestWc - winningChances(pv));
        shapes.push({
          orig: uci.slice(0, 2),
          dest: uci.slice(2, 4),
          brush: 'paleBlue',
          opacityFactor: Math.max(0.2, 1 - gap * 2.5),
        });
      });
    }

    if (effectiveThreatMode) {
      const threatUci = threatPvs[0]?.uciMoves?.[0];
      if (threatUci && threatUci.length >= 4) {
        const key = threatUci.slice(0, 4);
        if (!seen.has(key)) {
          seen.add(key);
          shapes.push({
            orig: threatUci.slice(0, 2),
            dest: threatUci.slice(2, 4),
            brush: 'paleRed',
          });
        }
      }
    }

    if (hoveredPvUci && hoveredPvUci.length >= 4) {
      const key = hoveredPvUci.slice(0, 4);
      if (!seen.has(key)) {
        shapes.push({
          orig: hoveredPvUci.slice(0, 2),
          dest: hoveredPvUci.slice(2, 4),
          brush: effectiveThreatMode ? 'paleRed' : 'paleBlue',
        });
      }
    }
    return shapes;
  }, [
    enableStockfish,
    isChapterLocked,
    assistSuppressed,
    showBestMoveArrow,
    cevalPvs,
    effectiveThreatMode,
    threatPvs,
    hoveredPvUci,
  ]);

  // The engines themselves: headless (render null), mounted in both layouts
  // so eval / practice keep running whichever tab is visible.
  const cevalEngineElement = (
    <StockfishEngine
      fen={cevalFen}
      depth={CEVAL_MAX_DEPTH}
      variations={practiceMode ? 1 : cevalMultiPv}
      engineOn={enableStockfish && !cevalGameOver}
      onResult={handleCevalInfo}
    />
  );

  // Third engine instance: the practice OPPONENT. Separate from the analysis
  // engine above because it is deliberately weakened (UCI_Elo) and searches a
  // much shorter budget — mixing the two would mean the eval bar, the
  // best-move arrow and the move verdicts all inherited the opponent's
  // handicap, which would make the coaching wrong rather than just easy.
  // Only SEARCHES when it is actually the opponent's turn — it used to search
  // every position, including the student's own, putting three Stockfish WASM
  // workers (analysis at depth 24/10s, this one, and threat when on) on the
  // cores for every move. Nothing downstream wants a reply for a position the
  // student is to move in anyway: usePracticeGame discards it.
  //
  // Note this drives `searchEnabled`, NOT `engineOn`. Gating the worker's
  // LIFECYCLE on whose turn it is destroys and respawns it every single ply,
  // so each reply then waits on a fresh worker spawn, WASM instantiation and
  // uci/isready handshake — which looks exactly like the engine having stopped
  // playing. `engineOn` stays keyed to practice mode itself, so the worker is
  // built once per game and only its search stops and starts.
  const opponentToMove =
    practiceMode &&
    Boolean(practiceStudentColor) &&
    cevalFen.split(' ')[1] !== practiceStudentColor;

  const opponentEngineElement = (
    <StockfishEngine
      fen={cevalFen}
      depth={practiceDepth ?? 8}
      variations={1}
      movetime={practiceMovetime ?? 800}
      elo={practiceElo ?? undefined}
      engineOn={practiceMode && enableStockfish && !cevalGameOver}
      searchEnabled={opponentToMove}
      onResult={handleOpponentInfo}
      onBestMove={handleOpponentBestMove}
    />
  );

  // Second, independent engine instance dedicated to threat mode — only
  // runs while threat mode is actually on, searching the opponent's reply
  // position (threatFen) without touching cevalFen/cevalPvs above.
  const threatEngineElement = (
    <StockfishEngine
      fen={threatFen || cevalFen}
      depth={CEVAL_MAX_DEPTH}
      variations={1}
      engineOn={effectiveThreatMode && !cevalGameOver}
      onResult={handleThreatCevalInfo}
    />
  );

  // "x" toggles threat mode, matching lichess's analysis keymap.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        return;
      }
      if (event.key === 'x' || event.key === 'X') {
        toggleThreatMode();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleThreatMode]);

  return {
    enableStockfish,
    setEnableStockfish,
    cevalFen,
    threatFen,
    cevalPvs,
    cevalDepth,
    cevalMultiPv,
    setCevalMultiPv,
    showBestMoveArrow,
    setShowBestMoveArrow,
    effectiveThreatMode,
    toggleThreatMode,
    handleCevalToggle,
    setHoveredPvUci,
    nextBestMoveUci,
    opponentBestMoveUci: opponentPvUci,
    engineAutoShapes,
    cevalEngineElement,
    threatEngineElement,
    opponentEngineElement,
  };
}
